import { ReviewCanvasContext } from 'modules/match-video/domain/interfaces';

/** An element's box on the 1920×1080 frame, px. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Traces CSS `clip-path: polygon(...)` of a box: points as fractions of its width and height, `[0.62, 0]` = `62% 0`.
 * Begins a new path; fill or clip it afterwards.
 */
export function tracePolygon(ctx: ReviewCanvasContext, box: Box, points: readonly (readonly [number, number])[]): void {
  ctx.beginPath();
  points.forEach(([fx, fy], i) => {
    const x = box.left + fx * box.width;
    const y = box.top + fy * box.height;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
}

/**
 * CSS `linear-gradient(<angle>, ...)` over a box. The gradient line passes through the box centre in the angle's
 * direction (0deg — up, 90deg — right) and is long enough for the corners to get the end colours:
 * `|W·sin A| + |H·cos A|` (CSS Images 3, «linear-gradient() syntax»).
 */
export function cssLinearGradient(
  ctx: ReviewCanvasContext,
  box: Box,
  angleDeg: number,
  stops: readonly (readonly [number, string])[],
): CanvasGradient {
  const a = (angleDeg * Math.PI) / 180;
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  const half = (Math.abs(box.width * dx) + Math.abs(box.height * dy)) / 2;
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  const gradient = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);
  stops.forEach(([offset, color]) => gradient.addColorStop(offset, color));
  return gradient;
}

/** `transform: skewX(<deg>)` around the origin the context is translated to. */
export function skewX(ctx: ReviewCanvasContext, deg: number): void {
  ctx.transform(1, 0, Math.tan((deg * Math.PI) / 180), 1, 0, 0);
}

/** Scales around a point, like `scale` with `transform-origin` there. */
export function scaleAround(ctx: ReviewCanvasContext, x: number, y: number, sx: number, sy: number = sx): void {
  ctx.translate(x, y);
  ctx.scale(sx, sy);
  ctx.translate(-x, -y);
}
